import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowDownUp, ChevronRight, Info } from 'lucide-react';

import { loadCohortOverview } from './api';
import { tx } from './i18n';
import { FilterMenu, KpiRow, NUMBER, Panel, counted } from './MobileParts';
import { Empty, Failed, Head, Loading, SearchField } from './parts';
import { useLiveResource } from './useLive';
import './cohorts.css';

// Research > Cohorts: who is in each project and subproject, their age and sex,
// which omics layers they have, how the layers overlap and how their participants
// compare. Choosing layers narrows the whole page to the participants who have
// every one of them. The scope and the layers live in the address, so a link
// opens the same view. The server counts; this page only draws.

const text = (key, values) => tx(`ui.cohorts.${key}`, values);
const layerName = (code) => text(`layer_${code.toLowerCase()}`);
const TOP_COMBINATIONS = 12;
const FIRST_ROWS = 25;
const SORTS = ['participants', 'name', 'age'];

// A share as a whole percent, or nothing when there is nothing to divide.
function share(part, whole) {
  if (!whole) return '';
  const value = (part / whole) * 100;
  return `${value > 0 && value < 1 ? '<1' : Math.round(value)}%`;
}

function ageLabel(group) {
  return group.to === null ? text('age_over', { from: group.from }) : text('age_range', { from: group.from, to: group.to - 1 });
}

function groupName(group) {
  return group.name || group.code || text('unnamed');
}

function ageFigure(data) {
  if (data.participants === 0) return text('nobody');
  return data.age.median === null ? text('not_recorded') : text('years', { count: data.age.median });
}

function Crumbs({ data, onScope }) {
  const steps = [{ key: 'all', label: text('all_projects'), scope: {} }];
  if (data.scope.project) steps.push({ key: 'project', label: data.scope.project.name, scope: { project: data.scope.project.id } });
  if (data.scope.subproject) {
    steps.push({ key: 'subproject', label: groupName(data.scope.subproject), scope: { project: data.scope.project.id, subproject: data.scope.subproject.id } });
  }
  return (
    <ol className="xo-coh-crumbs">
      {steps.map((step, index) => (
        <li key={step.key}>
          {index > 0 && <ChevronRight aria-hidden="true" />}
          {index === steps.length - 1 ? <strong aria-current="location">{step.label}</strong>
            : <button type="button" onClick={() => onScope(step.scope)}>{step.label}</button>}
        </li>
      ))}
    </ol>
  );
}

// The omics layers as switches: each one on keeps only the participants who have it.
function LayerSwitches({ layers, chosen, onChange }) {
  const toggle = (code) => onChange(chosen.includes(code) ? chosen.filter((item) => item !== code) : [...chosen, code]);
  return (
    <div className="xo-coh-switches" role="group" aria-label={text('layers_filter')}>
      <span>{text('who_have')}</span>
      {layers.map((code) => {
        const on = chosen.includes(code);
        return (
          <button key={code} type="button" aria-pressed={on} className={on ? 'is-on' : ''} onClick={() => toggle(code)}>
            <b>{code}</b>{layerName(code)}
          </button>
        );
      })}
      {chosen.length > 0 && <button type="button" className="xo-link" onClick={() => onChange([])}>{text('everyone')}</button>}
    </div>
  );
}

function Layers({ data, chosen, onToggle }) {
  const most = Math.max(1, ...data.omics.map((layer) => layer.participants));
  return (
    <ol className="xo-coh-layers">
      {data.omics.map((layer) => {
        const on = chosen.includes(layer.code);
        let detail = text('analysed_all');
        if (layer.participants === 0) detail = text('layer_none');
        else if (layer.analysed === 0) detail = text('received_only');
        else if (layer.analysed < layer.participants) detail = text('analysed_some', { count: NUMBER.format(layer.analysed) });
        return (
          <li key={layer.code}>
            <button type="button" className={on ? 'is-on' : ''} aria-pressed={on} onClick={() => onToggle(layer.code)}>
              <span className="xo-coh-layer-name"><strong>{layerName(layer.code)}</strong><small>{detail}</small></span>
              <span className="xo-coh-meter"><i style={{ width: `${(layer.participants / most) * 100}%` }} /></span>
              <span className="xo-coh-figure"><b>{NUMBER.format(layer.participants)}</b><em>{share(layer.participants, data.participants)}</em></span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

// Age groups as a pyramid: men to the left, women to the right, oldest on top.
function Pyramid({ data }) {
  const groups = [...data.age.groups].reverse();
  const most = Math.max(1, ...groups.flatMap((group) => [group.female, group.male]));
  const others = data.sex.other + data.sex.unknown;
  return (
    <div className="xo-coh-pyramid">
      <div className="xo-coh-pyramid-key" aria-hidden="true">
        <span><i className="is-male" />{text('men')} <b>{NUMBER.format(data.sex.male)}</b></span>
        <span><i className="is-female" />{text('women')} <b>{NUMBER.format(data.sex.female)}</b></span>
      </div>
      {groups.map((group) => (
        <div key={group.from} className="xo-coh-pyramid-row"
          title={text('pyramid_row', { age: ageLabel(group), women: NUMBER.format(group.female), men: NUMBER.format(group.male) })}>
          <span className="xo-coh-side is-male"><em>{group.male ? NUMBER.format(group.male) : ''}</em><i style={{ width: `${(group.male / most) * 100}%` }} /></span>
          <span className="xo-coh-age">{ageLabel(group)}</span>
          <span className="xo-coh-side is-female"><i style={{ width: `${(group.female / most) * 100}%` }} /><em>{group.female ? NUMBER.format(group.female) : ''}</em></span>
        </div>
      ))}
      {(others > 0 || data.age.unrecorded > 0) && (
        <p className="xo-mob-footnote"><Info aria-hidden="true" />
          {text('pyramid_footnote', { others: NUMBER.format(others), unrecorded: NUMBER.format(data.age.unrecorded) })}
        </p>
      )}
    </div>
  );
}

// Which layers participants have together: a bar for each exact combination,
// largest first, and under it a dot for each layer in it. Choosing one keeps the
// participants who have at least those layers.
function Overlap({ data, onPick }) {
  const combinations = data.overlap.slice(0, TOP_COMBINATIONS);
  const most = Math.max(1, ...combinations.map((combination) => combination.participants));
  const shown = combinations.reduce((sum, combination) => sum + combination.participants, 0);
  const rest = data.withOmics - shown;
  if (combinations.length === 0) return <Empty title={text('overlap_empty')} />;
  return (
    <>
      <div className="xo-coh-upset" style={{ '--rows': data.layers.length }}>
        <div className="xo-coh-upset-names" aria-hidden="true">
          {data.layers.map((code) => <span key={code}>{layerName(code)}</span>)}
        </div>
        <div className="xo-coh-upset-columns">
          {combinations.map((combination) => {
            const at = data.layers.map((code, index) => (combination.layers.includes(code) ? index : -1)).filter((index) => index >= 0);
            const names = combination.layers.map(layerName).join(', ');
            return (
              <button key={combination.layers.join('+')} type="button" className="xo-coh-upset-column" onClick={() => onPick(combination.layers)}
                title={text('overlap_column', { layers: names, count: NUMBER.format(combination.participants) })}
                aria-label={text('overlap_column', { layers: names, count: NUMBER.format(combination.participants) })}>
                <span className="xo-coh-upset-bar"><em>{NUMBER.format(combination.participants)}</em><i style={{ height: `${(combination.participants / most) * 100}%` }} /></span>
                <span className="xo-coh-upset-dots">
                  {at.length > 1 && <b style={{ top: `calc(${at[0] + 0.5} * var(--dot-row))`, height: `calc(${at[at.length - 1] - at[0]} * var(--dot-row))` }} />}
                  {data.layers.map((code) => <i key={code} className={combination.layers.includes(code) ? 'is-on' : ''} />)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <p className="xo-mob-footnote"><Info aria-hidden="true" />
        {rest > 0 ? text('overlap_footnote_rest', { count: NUMBER.format(rest) }) : text('overlap_footnote')}
      </p>
    </>
  );
}

// Each layer's participants side by side: how many, their age and sex, and which
// share of them also have each other layer.
function Compare({ data }) {
  const rows = data.omics.filter((layer) => layer.participants > 0);
  if (rows.length === 0) return <Empty title={text('layer_none')} />;
  return (
    <div className="xo-coh-scroll">
      <table className="xo-coh-table">
        <thead>
          <tr>
            <th scope="col">{text('column_layer')}</th>
            <th scope="col" className="is-number">{text('column_participants')}</th>
            <th scope="col" className="is-number">{text('column_median_age')}</th>
            <th scope="col" className="is-number">{text('column_women')}</th>
            {rows.map((layer) => <th key={layer.code} scope="col" className="is-cell" title={text('also_have', { layer: layerName(layer.code) })}>{layer.code}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((layer) => (
            <tr key={layer.code}>
              <th scope="row"><strong>{layerName(layer.code)}</strong><small>{layer.code}</small></th>
              <td className="is-number">{NUMBER.format(layer.participants)}</td>
              <td className="is-number">{layer.medianAge ?? ''}</td>
              <td className="is-number">{share(layer.female, layer.female + layer.male)}</td>
              {rows.map((other) => {
                const value = layer.alsoHave[other.code] / layer.participants;
                const self = other.code === layer.code;
                return (
                  <td key={other.code} className={`is-cell${self ? ' is-self' : ''}${value >= 0.55 ? ' is-strong' : ''}`} style={{ '--v': self ? 0 : value }}
                    title={self ? undefined : text('also_have_cell', { share: share(layer.alsoHave[other.code], layer.participants), layer: layerName(layer.code), other: layerName(other.code) })}>
                    {self ? '' : share(layer.alsoHave[other.code], layer.participants)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// One level down from the scope: the projects, or the subprojects of the project
// open. Each layer's cell is darker where more of the group has it.
function Groups({ data, onOpen }) {
  const [sort, setSort] = useState('participants');
  const [search, setSearch] = useState('');
  const [all, setAll] = useState(false);
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const found = term === '' ? data.groups
      : data.groups.filter((group) => [group.name, group.code].some((value) => String(value || '').toLowerCase().includes(term)));
    const byName = (left, right) => groupName(left).localeCompare(groupName(right));
    const order = {
      participants: (left, right) => right.participants - left.participants || byName(left, right),
      name: byName,
      age: (left, right) => (right.medianAge ?? -1) - (left.medianAge ?? -1) || byName(left, right),
    }[sort];
    return [...found].sort(order);
  }, [data.groups, search, sort]);
  const shown = all ? rows : rows.slice(0, FIRST_ROWS);
  const subprojects = data.scope.project !== null;
  return (
    <>
      <div className="xo-coh-groups-bar">
        <SearchField className="xo-coh-search" value={search} onChange={setSearch} autoComplete="off"
          placeholder={text(subprojects ? 'search_subprojects' : 'search_projects')} />
        <FilterMenu icon={ArrowDownUp} label={text('sort')} value={sort} onChange={setSort}
          options={SORTS.map((key) => ({ value: key, label: text(`sort_${key}`) }))} />
      </div>
      {rows.length === 0 ? <Empty title={text('no_match')} /> : (
        <div className="xo-coh-scroll">
          <table className="xo-coh-table is-groups">
            <thead>
              <tr>
                <th scope="col">{text(subprojects ? 'column_subproject' : 'column_project')}</th>
                <th scope="col" className="is-number">{text('column_participants')}</th>
                {data.layers.map((code) => <th key={code} scope="col" className="is-cell" title={layerName(code)}>{code}</th>)}
                <th scope="col" className="is-number">{text('column_median_age')}</th>
                <th scope="col" className="is-number">{text('column_women')}</th>
                <th scope="col" aria-label={text('open')} />
              </tr>
            </thead>
            <tbody>
              {shown.map((group) => (
                <tr key={group.id} className={group.participants === 0 ? 'is-empty' : ''} onClick={() => onOpen(group)}>
                  <th scope="row">
                    <button type="button" onClick={(event) => { event.stopPropagation(); onOpen(group); }}>
                      <strong>{groupName(group)}</strong>{group.code && group.name && <small>{group.code}</small>}
                    </button>
                  </th>
                  <td className="is-number">{NUMBER.format(group.participants)}</td>
                  {data.layers.map((code) => {
                    const value = group.participants ? group.omics[code] / group.participants : 0;
                    return (
                      <td key={code} className={`is-cell${value >= 0.55 ? ' is-strong' : ''}`} style={{ '--v': value }}
                        title={group.omics[code] ? text('group_cell', { count: NUMBER.format(group.omics[code]), share: share(group.omics[code], group.participants), layer: layerName(code) }) : undefined}>
                        {group.omics[code] ? NUMBER.format(group.omics[code]) : ''}
                      </td>
                    );
                  })}
                  <td className="is-number">{group.medianAge ?? ''}</td>
                  <td className="is-number">{share(group.female, group.female + group.male)}</td>
                  <td className="is-end"><ChevronRight aria-hidden="true" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!all && rows.length > FIRST_ROWS && (
        <button type="button" className="xo-btn-2 xo-coh-more" onClick={() => setAll(true)}>{text('show_all', { count: NUMBER.format(rows.length) })}</button>
      )}
    </>
  );
}

export default function XomicsCohorts() {
  const [params, setParams] = useSearchParams();
  const project = params.get('project') || '';
  const subproject = project ? params.get('subproject') || '' : '';
  const layersKey = params.get('layers') || '';
  const chosen = useMemo(() => layersKey.split(',').filter(Boolean), [layersKey]);

  const load = useCallback(() => loadCohortOverview({ project, subproject, layers: layersKey }), [project, subproject, layersKey]);
  const overview = useLiveResource(['projects', 'uploads', 'jobs'], load, { reconcileMs: 300000 });
  const data = overview.data;
  const current = data !== null && String(data.scope.project?.id ?? '') === project
    && String(data.scope.subproject?.id ?? '') === subproject && data.scope.layers.join(',') === layersKey;

  // Opening a project or subproject is a step Back undoes; the chosen layers stay.
  const scopeTo = useCallback((scope) => {
    const next = new URLSearchParams();
    if (scope.project) next.set('project', String(scope.project));
    if (scope.subproject) next.set('subproject', String(scope.subproject));
    if (layersKey) next.set('layers', layersKey);
    setParams(next);
  }, [layersKey, setParams]);
  const setLayers = useCallback((codes) => {
    setParams((currentParams) => {
      const next = new URLSearchParams(currentParams);
      const ordered = data ? data.layers.filter((code) => codes.includes(code)) : codes;
      if (ordered.length === 0) next.delete('layers'); else next.set('layers', ordered.join(','));
      return next;
    }, { replace: true });
  }, [data, setParams]);
  const toggleLayer = (code) => setLayers(chosen.includes(code) ? chosen.filter((item) => item !== code) : [...chosen, code]);
  const openGroup = (group) => (group.kind === 'project' ? scopeTo({ project: group.id }) : scopeTo({ project, subproject: group.id }));

  const sexed = data ? data.sex.female + data.sex.male : 0;
  return (
    <div className="xo-mobile-page xo-coh-page">
      <Head title={text('title')} subtitle={text('subtitle')} />
      {overview.loading && data === null && <Loading label={text('loading')} />}
      {overview.error && data === null && <Failed message={overview.error} onRetry={overview.refresh} />}
      {data && (
        <div className={`xo-coh-body${current ? '' : ' is-stale'}`} aria-busy={!current}>
          {overview.error && <p className="xo-alert t-warn" role="alert">{overview.error}</p>}
          <div className="xo-coh-scope">
            <Crumbs data={data} onScope={scopeTo} />
            <LayerSwitches layers={data.layers} chosen={chosen} onChange={setLayers} />
          </div>

          <KpiRow items={[
            { label: text('kpi_participants'), value: NUMBER.format(data.participants),
              detail: chosen.length > 0 ? text('kpi_participants_of', { total: NUMBER.format(data.total) }) : text('kpi_participants_detail') },
            { label: text('kpi_with_omics'), value: NUMBER.format(data.withOmics),
              detail: data.participants ? text('kpi_share_of_participants', { share: share(data.withOmics, data.participants) }) : '' },
            { label: text('kpi_multi_omics'), value: NUMBER.format(data.multiOmics),
              detail: data.participants ? text('kpi_share_of_participants', { share: share(data.multiOmics, data.participants) }) : '' },
            { label: text('kpi_median_age'), value: ageFigure(data),
              detail: data.participants ? counted('ui.cohorts.kpi_age_missing', data.age.unrecorded) : '' },
            { label: text('kpi_women'), value: data.participants === 0 ? text('nobody') : sexed ? share(data.sex.female, sexed) : text('not_recorded'),
              detail: data.participants ? text('kpi_sex_detail', { women: NUMBER.format(data.sex.female), men: NUMBER.format(data.sex.male) }) : '' },
          ]} />

          {data.participants === 0 ? (
            <Panel><Empty title={text('empty')} detail={chosen.length > 0 ? text('empty_layers') : text('empty_scope')} /></Panel>
          ) : (
            <>
              <div className="xo-mob-row is-halves">
                <Panel title={text('layers_title')} detail={text('layers_detail')}>
                  <Layers data={data} chosen={chosen} onToggle={toggleLayer} />
                </Panel>
                <Panel title={text('age_title')} detail={text('age_detail')}>
                  <Pyramid data={data} />
                </Panel>
              </div>
              <Panel title={text('overlap_title')} detail={text('overlap_detail')}>
                <Overlap data={data} onPick={setLayers} />
              </Panel>
              <Panel title={text('compare_title')} detail={text('compare_detail')}>
                <Compare data={data} />
              </Panel>
            </>
          )}

          {data.groups.length > 0 && (
            <Panel title={text(data.scope.project ? 'subprojects_title' : 'projects_title')}
              detail={counted(data.scope.project ? 'ui.cohorts.subprojects_detail' : 'ui.cohorts.projects_detail', data.groups.length)}>
              <Groups key={`${project}-${subproject}`} data={data} onOpen={openGroup} />
            </Panel>
          )}
        </div>
      )}
    </div>
  );
}
